import { useQuery } from '@tanstack/react-query'
import { alertApi, clusterApi, networkProblemApi, customerApi, discoveryApi, machineApi, maintenanceSessionApi, networkApi, overviewApi, projectApi, provisionJobApi, switchApi, zoneApi } from '../api/client'

// Shared queries of the operator area: one cache per resource, so lists,
// detail pages and the global search use the same data.

export function useMachines() {
  // jobs run in the background: refresh often while one is active
  return useQuery({
    queryKey: ['machines'],
    queryFn: machineApi.list,
    // idle every 10 s: what someone else starts shows soon on every open page
    refetchInterval: (q) => (q.state.data?.some((m) => m.active_job || m.power_op?.state === 'running') ? 3000 : 10000),
  })
}

export function useClusters() {
  return useQuery({ queryKey: ['clusters'], queryFn: clusterApi.list })
}

export function useOverview() {
  return useQuery({ queryKey: ['overview'], queryFn: overviewApi.get, refetchInterval: 60000 })
}

// the same for everyone; while a test or a check runs, its answer is seconds away
export function useNetworkProblems(site?: string) {
  return useQuery({
    queryKey: ['network-problems', site || ''],
    queryFn: () => networkProblemApi.list(site),
    refetchInterval: (q) => (q.state.data?.some((p) => p.cable_running || p.check === 'checking') ? 2000 : 10000),
  })
}

export function useMaintenanceWindow() {
  return useQuery({ queryKey: ['maintenance-window'], queryFn: overviewApi.window, refetchInterval: 300000 })
}

export function useSites() {
  return useQuery({ queryKey: ['sites'], queryFn: clusterApi.sites })
}

export function useNetworks() {
  return useQuery({ queryKey: ['networks'], queryFn: () => networkApi.list() })
}

export function useSwitchPorts() {
  return useQuery({ queryKey: ['switches'], queryFn: switchApi.list, refetchInterval: 60000 })
}

export function useDiscovery() {
  return useQuery({ queryKey: ['discovery'], queryFn: discoveryApi.list, refetchInterval: 60000 })
}

export function useJobs(params: Parameters<typeof provisionJobApi.list>[0] = {}) {
  return useQuery({
    queryKey: ['jobs', params],
    queryFn: () => provisionJobApi.list(params),
    refetchInterval: (q) => (q.state.data?.some((j) => ['queued', 'pending', 'running'].includes(j.state)) ? 4000 : 10000),
  })
}

export function useFirmwareUpdates() {
  return useQuery({ queryKey: ['firmware-updates'], queryFn: machineApi.firmwareUpdates, refetchInterval: 120000 })
}

export function useProjects() {
  return useQuery({ queryKey: ['projects'], queryFn: () => projectApi.list() })
}

export function useCustomers() {
  return useQuery({ queryKey: ['customers'], queryFn: customerApi.list })
}

export function useZones() {
  // the maintenance lock changes while jobs run
  return useQuery({ queryKey: ['zones'], queryFn: zoneApi.list, refetchInterval: (q) => (q.state.data?.some((z) => z.maintenance !== 'free') ? 10000 : 60000) })
}

export function useMaintenanceSessions() {
  // what a colleague starts shows soon in everyone's top bar
  return useQuery({ queryKey: ['maintenance-sessions'], queryFn: maintenanceSessionApi.list, refetchInterval: (q) => (q.state.data?.some((s) => !s.ended_at) ? 10000 : 30000) })
}

export function useAlerts(all = false) {
  return useQuery({ queryKey: ['alerts', all], queryFn: () => alertApi.list(all), refetchInterval: 60000 })
}

// usePlatformLacking: what the machine's platform cannot do, function → why (docs/PLATFORMS.md,
// "BMC drivers"): a capability of the BMC driver, setup-console, flash-bmc, flash-bios. The
// buttons stay, disabled with the reason.
export function usePlatformLacking(machineId: string) {
  const { data } = useQuery({ queryKey: ['machine-platform', machineId], queryFn: () => machineApi.platform(machineId), staleTime: 300_000, enabled: !!machineId })
  return (fn: string): string | undefined => data?.lacking?.[fn]
}
